import { useContext, useMemo, useCallback } from 'react';
import NotificationContext from '../context/NotificationContext';
import notificationService from '../services/notificationService';

// Hook principal - re-exporta o context com utilities extras
export function useNotifications() {
  const context = useContext(NotificationContext);
  
  if (!context) {
    throw new Error('useNotifications deve ser usado dentro de NotificationProvider');
  }
  
  return context;
}

// Hook para operações específicas do sininho
export function useBellNotifications() {
  const { 
    bellNotifications, 
    bellCount, 
    markAsRead, 
    markAllAsRead,
    openModal,
    isMarkingAsRead 
  } = useNotifications();

  const hasNotifications = bellCount > 0;

  const handleMarkAsRead = useCallback((notification) => {
    return markAsRead(notification, false); // false = não é do modal
  }, [markAsRead]);

  const handleMarkAllAsRead = useCallback(() => {
    return markAllAsRead(false); // false = não é do modal
  }, [markAllAsRead]);

  return {
    notifications: bellNotifications,
    count: bellCount,
    hasNotifications,
    markAsRead: handleMarkAsRead,
    markAllAsRead: handleMarkAllAsRead,
    openModal,
    isMarkingAsRead
  };
}

// Hook para operações específicas do modal
export function useModalNotifications() {
  const { 
    modalNotifications, 
    showModal, 
    markAsRead, 
    markAllAsRead,
    closeModal,
    isMarkingAsRead,
    userPreferences
  } = useNotifications();

  const hasNotifications = modalNotifications.length > 0;
  const isObligatory = userPreferences.alerta_tela === 1;

  const handleMarkAsRead = useCallback((notification) => {
    return markAsRead(notification, true); // true = é do modal
  }, [markAsRead]);

  const handleMarkAllAsRead = useCallback(() => {
    return markAllAsRead(true); // true = é do modal
  }, [markAllAsRead]);

  const handleClose = useCallback(() => {
    if (isObligatory && hasNotifications) {
      return false; // Não pode fechar - modal obrigatório
    }
    return closeModal();
  }, [isObligatory, hasNotifications, closeModal]);

  // Notificações ordenadas por data (mais recentes primeiro)
  const sortedNotifications = useMemo(() => {
    return [...modalNotifications].sort((a, b) => {
      const dateA = new Date(`${a.data_erro} ${a.hora_erro}`);
      const dateB = new Date(`${b.data_erro} ${b.hora_erro}`);
      return dateB - dateA;
    });
  }, [modalNotifications]);

  return {
    notifications: sortedNotifications,
    showModal,
    hasNotifications,
    isObligatory,
    markAsRead: handleMarkAsRead,
    markAllAsRead: handleMarkAllAsRead,
    closeModal: handleClose,
    isMarkingAsRead
  };
}

// Hook para configurações do usuário
export function useNotificationPreferences() {
  const { userPreferences, dispatch, ACTIONS } = useNotifications();

  const updatePreferences = useCallback((newPreferences) => {
    dispatch({
      type: ACTIONS.SET_USER_PREFERENCES,
      payload: newPreferences
    });
  }, [dispatch, ACTIONS]);

  const toggleModalMode = useCallback(() => {
    const newValue = userPreferences.alerta_tela === 1 ? 0 : 1;
    updatePreferences({ alerta_tela: newValue });
  }, [userPreferences.alerta_tela, updatePreferences]);

  const toggleFilter = useCallback(() => {
    const newValue = userPreferences.filtrar_notificacao === 1 ? 0 : 1;
    updatePreferences({ filtrar_notificacao: newValue });
  }, [userPreferences.filtrar_notificacao, updatePreferences]);

  return {
    preferences: userPreferences,
    updatePreferences,
    toggleModalMode,
    toggleFilter,
    isModalMode: userPreferences.alerta_tela === 1,
    isFilterActive: userPreferences.filtrar_notificacao === 1
  };
}

// Hook para status de conexão
export function useNotificationConnection() {
  const { isConnected, lastUpdate } = useNotifications();

  const status = useMemo(() => {
    return {
      isConnected,
      lastUpdate: new Date(lastUpdate),
      statusText: isConnected ? 'Conectado' : 'Desconectado',
      statusColor: isConnected ? 'green' : 'red'
    };
  }, [isConnected, lastUpdate]);

  return status;
}

// Hook utilitário para formatação
export function useNotificationUtils() {
  const formatDate = useCallback((dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  }, []);

  const formatTime = useCallback((timeString) => {
    try {
      const [hours, minutes] = timeString.split(':');
      return `${hours}:${minutes}`;
    } catch {
      return timeString;
    }
  }, []);

  const getNotificationKey = useCallback((notification) => {
    return notificationService.getErrorKey(notification);
  }, []);

  const getUrgencyLevel = useCallback((notification) => {
    // Pode ser expandido com lógica de prioridade
    return 'normal';
  }, []);

  return {
    formatDate,
    formatTime,
    getNotificationKey,
    getUrgencyLevel
  };
}

export default useNotifications;